import React, { useRef, useState, useEffect } from 'react';

interface AutoScrollTitleProps {
  text?: string;
  children?: React.ReactNode;
  className?: string;
  speed?: number; // pixels per second
  delay?: number; // delay in ms before start
  alwaysScroll?: boolean;
}

export const AutoScrollTitle: React.FC<AutoScrollTitleProps> = ({
  text = '',
  children,
  className = '',
  speed = 28,
  delay = 350,
  alwaysScroll = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [overflowDistance, setOverflowDistance] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  // Só roda a animação contínua quando o card está realmente visível na tela — evita dezenas de
  // animações CSS rodando pra sempre em segundo plano em listas grandes (até 80 canais na grade
  // ao mesmo tempo), especialmente relevante em tablets com GPU mais fraca.
  const [isInViewport, setIsInViewport] = useState(!alwaysScroll);

  useEffect(() => {
    if (!alwaysScroll || typeof IntersectionObserver === 'undefined' || !containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInViewport(entry.isIntersecting),
      { rootMargin: '100px' } // começa/mantém a animação um pouco antes de entrar na tela, pra não "piscar"
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [alwaysScroll]);

  useEffect(() => {
    let animId: number;
    const checkOverflow = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const textWidth = textRef.current.scrollWidth;
        const diff = textWidth - containerWidth;
        if (diff > 4) {
          setIsOverflowing(true);
          setOverflowDistance(diff);
        } else {
          setIsOverflowing(false);
          setOverflowDistance(0);
        }
      }
    };

    // Check immediately and on next frame (after DOM layout / CSS calculations)
    checkOverflow();
    animId = requestAnimationFrame(checkOverflow);
    const timer = setTimeout(checkOverflow, 100);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });
      resizeObserver.observe(containerRef.current);
      if (textRef.current) {
        resizeObserver.observe(textRef.current);
      }
    }

    window.addEventListener('resize', checkOverflow);
    return () => {
      cancelAnimationFrame(animId);
      clearTimeout(timer);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [text, children]);

  const duration = Math.max(3, overflowDistance / speed);

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`w-full min-w-0 overflow-hidden relative whitespace-nowrap select-none ${
        isOverflowing ? 'title-marquee-container' : ''
      } ${isHovered ? 'title-marquee-active' : ''} ${
        isOverflowing && alwaysScroll && isInViewport ? 'title-marquee-always' : ''
      }`}
      title={text}
    >
      <span
        ref={textRef}
        className={`inline-block whitespace-nowrap transition-transform ${className} ${
          isOverflowing ? 'title-marquee-text' : 'truncate block'
        }`}
        style={
          isOverflowing
            ? ({
                '--scroll-dist': `-${overflowDistance + 12}px`,
                '--scroll-duration': `${duration}s`,
                '--scroll-delay': `${delay}ms`,
              } as React.CSSProperties)
            : undefined
        }
      >
        {children || text}
      </span>
    </div>
  );
};
